// ---------------------------------------------------------------------------
// Matcher do menu antigo (AppMenu): fonte da verdade = ROTA ATUAL, não o clique.
//
// Resolve, a partir do location.pathname, QUAL link de item do menu está ativo — cobrindo:
//   - listagem (o próprio link do item)               ex.: /calleds
//   - subrotas do mesmo caminho                        ex.: /marketing/new  (casa /marketing)
//   - telas internas com caminho DIFERENTE da listagem ex.: /called-accompaniment/123 -> /calleds
//     (essas vêm do mapa MENU_ROUTE_ALIASES, pois "prefixo" sozinho não as cobre)
//
// Puro e sem React de propósito: fácil de testar e reusar (o breadcrumb da Fase 2 usa o mesmo trail).
// ---------------------------------------------------------------------------

import { MENU_ROUTE_ALIASES, MENU_LINK_EQUIVALENCE } from './menuRouteMap';

// Remove o prefixo de idioma (1º segmento, ex.: /pt, /en, /es) do pathname/link.
//   '/pt/called-accompaniment/123' -> '/called-accompaniment/123'
//   '/pt'                          -> '/'   (home)
export const stripLang = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return '';
  const clean = pathname.split('?')[0].split('#')[0];
  const parts = clean.split('/').filter(Boolean);
  if (parts.length <= 1) return '/';
  // lowercase: o matching é case-insensitive (rota /materialReports x link /materialreports).
  return ('/' + parts.slice(1).join('/')).toLowerCase();
};

// Normaliza links que apontam para a MESMA tela sob nomes diferentes (ex.: /calleds e /new-calleds,
// alternados pela flag enablenewscreencalled). Assim o alias pode apontar para um canônico só.
export const canonicalLink = (base) => (MENU_LINK_EQUIVALENCE && MENU_LINK_EQUIVALENCE[base]) || base;

// Compara dois links-base (já sem lang) considerando a equivalência acima.
export const isSameLink = (a, b) => !!a && !!b && canonicalLink(a) === canonicalLink(b);

// Achata a árvore de itens (pais + filhos) e devolve todos os links (base, sem lang) declarados.
export const flattenMenuLinks = (items) => {
  const links = [];
  const walk = (list) => {
    (list || []).forEach((it) => {
      if (it && typeof it.link === 'string' && it.link !== 'divider') {
        links.push(stripLang(it.link));
      }
      if (it && it.items && it.items.length) walk(it.items);
    });
  };
  walk(items);
  return links;
};

// true se `base` casa com o pathname `p`: igual, ou é um prefixo por SEGMENTO (evita que
// '/called' case '/called-accompaniment'; só casa '/called' ou '/called/...').
const segmentMatch = (p, base) => p === base || p.startsWith(base + '/');

// Resolve o link-base de menu ativo para o pathname atual. Retorna null se nada casar.
export const resolveActiveMenuLink = (pathname, menuLinks) => {
  const p = stripLang(pathname);
  if (!p) return null;

  // 1) Match direto contra os links do próprio menu — escolhe o MAIS ESPECÍFICO (base mais longa).
  let best = null;
  (menuLinks || []).forEach((base) => {
    if (base && segmentMatch(p, base) && (!best || base.length > best.length)) best = base;
  });
  if (best) return best;

  // 2) Alias: tela interna cujo caminho difere da listagem (mapa explícito). Também o mais específico.
  //    O valor do alias já é uma base sem lang (não aplicar stripLang aqui).
  let bestAliasTarget = null;
  let bestAliasLen = -1;
  Object.keys(MENU_ROUTE_ALIASES || {}).forEach((internal) => {
    if (segmentMatch(p, internal) && internal.length > bestAliasLen) {
      bestAliasLen = internal.length;
      bestAliasTarget = MENU_ROUTE_ALIASES[internal];
    }
  });
  return bestAliasTarget;
};

// Constrói a TRILHA (ancestrais → item ativo) para o breadcrumb, reusando a mesma árvore do menu.
// Retorna ex.: [{ pai }, { filho ativo }] ou [{ folha-raiz ativa }]. Vazio se nada casar.
export const buildTrail = (items, activeLink) => {
  const result = [];
  const dfs = (list, trail) => {
    for (const it of (list || [])) {
      if (!it || it.link === 'divider') continue;
      const here = trail.concat(it);
      if (typeof it.link === 'string' && isSameLink(stripLang(it.link), activeLink)) {
        here.forEach((node) => result.push(node));
        return true;
      }
      if (it.items && it.items.length && dfs(it.items, here)) return true;
    }
    return false;
  };
  dfs(items, []);
  return result;
};

// true se o item (ou algum descendente) é o link ativo — usado pra marcar/abrir o PAI.
export const itemHasActiveLink = (item, activeLink) => {
  if (!item || !activeLink) return false;
  if (typeof item.link === 'string' && item.link !== 'divider' && isSameLink(stripLang(item.link), activeLink)) return true;
  return (item.items || []).some((child) => itemHasActiveLink(child, activeLink));
};
